import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Crown, Globe, Mail, MapPin, Phone, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getCommunity, getCommunityMembers } from "../api/communityApi";
import { CommunityLogo, CommunityTypeChip } from "../components/Community/CommunityUI";
import { SOCIAL_NETWORKS } from "../components/Community/communityUtils";
import useCommunityMembership from "../components/Community/useCommunityMembership";
import { MemberAvatar, SkeletonBlock } from "../components/Teams/TeamUI";
import { formatRelativeTime, getDisplayName } from "../components/Teams/teamIdentity";

const MEMBERS_PAGE_SIZE = 20;

const ContactRow = ({ icon: Icon, href, children }) => (
  <li>
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="flex min-h-[44px] items-center gap-3 rounded-2xl bg-gray-50 px-3 text-sm text-gray-800 hover:bg-orange-50"
    >
      <Icon size={16} className="shrink-0 text-orange-500" aria-hidden="true" />
      <span className="truncate">{children}</span>
    </a>
  </li>
);

const CommunityDetail = () => {
  const { communityId } = useParams();
  const { t, i18n } = useTranslation();
  const { community: myCommunity } = useAuth();
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState({ total: 0, list: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [details, memberPage] = await Promise.all([
        getCommunity(communityId),
        getCommunityMembers(communityId, { limit: MEMBERS_PAGE_SIZE }),
      ]);
      setCommunity(details);
      setMembers({ total: memberPage.total, list: memberPage.members });
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const page = await getCommunityMembers(communityId, {
        limit: MEMBERS_PAGE_SIZE,
        offset: members.list.length,
      });
      setMembers((current) => ({ total: page.total, list: [...current.list, ...page.members] }));
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const { requestJoin, requestLeave, modal } = useCommunityMembership({ onChange: load });
  const isMine = myCommunity?.id === community?.id;
  const isAdmin = isMine && myCommunity?.role === "admin";
  const socialEntries = Object.entries(community?.socialLinks || {}).filter(([key]) => SOCIAL_NETWORKS[key]);

  if (isLoading && !community) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 pb-16">
        <SkeletonBlock className="h-56" />
        <SkeletonBlock className="h-64" />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="mx-auto max-w-5xl pb-16">
        <div role="alert" className="rounded-3xl bg-rose-50 p-6 text-rose-800 ring-1 ring-rose-200">
          <p className="font-semibold">{loadError || t("communityNotFound", { defaultValue: "Community not found." })}</p>
          <Link to="/communities" className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold underline">
            <ArrowLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
            {t("backToCommunities", { defaultValue: "All communities" })}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl pb-16">
      <Link to="/communities" className="mb-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-[#120239]">
        <ArrowLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
        {t("backToCommunities", { defaultValue: "All communities" })}
      </Link>

      {/* Hero */}
      <section className="overflow-hidden rounded-3xl bg-[#120239] text-white shadow-xl">
        <div className="relative px-6 py-8 md:px-10">
          <div
            className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-[#FF9200]/30 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
            <CommunityLogo community={community} size="lg" />
            <div className="min-w-0 flex-1">
              <CommunityTypeChip type={community.type} dark />
              <h1 className="mt-2 break-words text-3xl font-bold tracking-tight md:text-4xl">{community.name}</h1>
              {community.tagline && <p className="mt-1 text-sm text-slate-300 md:text-base">{community.tagline}</p>}
              <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-300">
                {(community.address || community.country?.name) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={14} aria-hidden="true" />
                    {[community.address, community.country?.name].filter(Boolean).join(", ")}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Users size={14} aria-hidden="true" />
                  {t("communityMembersCount", { defaultValue: "{{count}} members", count: community.memberCount })}
                </span>
              </p>
            </div>
          </div>

          <div className="relative mt-6">
            {!isMine ? (
              <button
                type="button"
                onClick={() => requestJoin(community)}
                className="min-h-[44px] w-full rounded-full bg-[#FF9200] px-6 text-sm font-bold text-[#120239] transition hover:bg-orange-400 sm:w-auto"
              >
                {t("joinCommunity", { defaultValue: "Join Community" })}
              </button>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-white/10 px-5 text-sm font-bold text-orange-200 ring-1 ring-orange-400/40">
                  {isAdmin
                    ? t("youAreCommunityAdmin", { defaultValue: "You're the admin" })
                    : t("youAreMember", { defaultValue: "You're a member" })}
                </span>
                {!isAdmin && (
                  <button
                    type="button"
                    onClick={requestLeave}
                    className="min-h-[44px] rounded-full px-5 text-sm font-semibold text-rose-200 ring-1 ring-rose-300/40 hover:bg-rose-500/10"
                  >
                    {t("leaveCommunity", { defaultValue: "Leave community" })}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {loadError && (
        <p role="alert" className="mt-4 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
          {loadError}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_20rem]">
        {/* Members */}
        <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <h2 className="flex items-center gap-2 text-base font-bold text-[#120239]">
            <Users size={18} className="text-orange-500" aria-hidden="true" />
            {t("communityMembers", { defaultValue: "Members" })}
            <span className="text-sm font-semibold text-gray-400">{members.total}</span>
          </h2>
          {members.list.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">
              {t("communityNoMembers", { defaultValue: "Be the first to join." })}
            </p>
          ) : (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {members.list.map((member) => (
                <li key={member.userId} className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2">
                  <MemberAvatar user={member} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1 truncate text-sm font-semibold text-gray-900">
                      {getDisplayName(member)}
                      {member.role === "admin" && (
                        <Crown size={14} className="shrink-0 text-amber-500" aria-label={t("communityAdmin", { defaultValue: "Admin" })} />
                      )}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {t("joinedAgo", {
                        defaultValue: "Joined {{when}}",
                        when: formatRelativeTime(member.joinedAt, i18n.language),
                      })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {members.list.length < members.total && (
            <button
              type="button"
              onClick={loadMore}
              disabled={isLoadingMore}
              className="mt-4 min-h-[44px] w-full rounded-full bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60"
            >
              {t("loadMore", { defaultValue: "Load more" })}
            </button>
          )}
        </section>

        {/* Contact */}
        <aside className="space-y-6">
          {(community.contactEmail || community.contactPhone || community.website || socialEntries.length > 0) && (
            <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h2 className="text-base font-bold text-[#120239]">{t("communityContact", { defaultValue: "Contact" })}</h2>
              <ul className="mt-3 space-y-2">
                {community.contactEmail && (
                  <ContactRow icon={Mail} href={`mailto:${community.contactEmail}`}>{community.contactEmail}</ContactRow>
                )}
                {community.contactPhone && (
                  <ContactRow icon={Phone} href={`tel:${community.contactPhone.replace(/[^+0-9]/g, "")}`}>
                    {community.contactPhone}
                  </ContactRow>
                )}
                {community.website && (
                  <ContactRow icon={Globe} href={community.website}>
                    {community.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </ContactRow>
                )}
              </ul>
              {socialEntries.length > 0 && (
                <ul className="mt-4 flex flex-wrap gap-2">
                  {socialEntries.map(([key, url]) => {
                    const { icon: Icon, label } = SOCIAL_NETWORKS[key];
                    return (
                      <li key={key}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          title={label}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#120239] text-white transition hover:bg-[#FF9200] hover:text-[#120239]"
                        >
                          <Icon size={18} aria-hidden="true" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </aside>
      </div>

      {modal}
    </div>
  );
};

export default CommunityDetail;
